/* ==========================================================================
   halo-upsell-cart.js  (v7.2 — strict Alb/Alb Opac colors, double-coat outfit flow,
                              slot validation, layered metadata,
                              deterministic priority, full-outfit rule)
   --------------------------------------------------------------------------
   Standalone cart sidebar upsell logic for the Halo theme on 1zero1.ro.
   Performance patch 2026-09-17: reuse cart-update data, deduplicate metadata
   and collection requests, and overlap needed accessory backfill requests.
   Recommendation rules and slot ordering are unchanged.

   How it works:
     - theme.js's halo.updateSidebarCart() dispatches a 'cart-update'
       custom event after every cart re-render.
     - This file listens for that event and rebuilds the upsell carousel.

   Load order (in theme.liquid):
     <script src="{{ 'theme.js'             | asset_url }}" defer="defer"></script>
     <script src="{{ 'halo-upsell-cart.js'  | asset_url }}" defer="defer"></script>

   --------------------------------------------------------------------------
   CHANGES FROM v4 — all fixes, in the order they matter
   --------------------------------------------------------------------------
   [FIX 1] STALE-BUILD GUARD
       Every runUpsell() gets a monotonically increasing build id. After
       each await, the build checks it is still the current one and bails
       out otherwise. Prevents a slow "cart had only a blouse" run from
       overwriting a fresh "blouse + pants" carousel.
       Cart-update events are also coalesced (50ms) so a burst of events
       triggers a single rebuild.

   [FIX 2] SLOT TYPE VALIDATION
       A product returned by /collections/{handle}?view=ajax-cart-upsell is
       now verified to actually BE the requested type. If a jeans product
       ends up inside halate-medicale-femei (bad automatic collection rule,
       wrong tagging, ...) it can no longer be served as a coat.
       Products whose type cannot be resolved at all are still kept, so the
       carousel does not empty out on incomplete metadata.

   [FIX 3] LAYERED METADATA (no more wholesale source selection)
       v4 did: `if (row.attrMeta) { use it; skip the metafield fetch; }`
       readMetaAttrs() returns an object if ANY attribute is present, so a
       cart row carrying only data-upsell-color blocked the metafield
       lookup and left type/gender unresolved. Sources are now MERGED
       field-by-field: attrs → ?view=upsell-meta → product_type → title.

   [FIX 4] DETERMINISTIC PRIORITY PER SLOT
       SLOT_CONFIG lets you pin an ordered model list per slot+gender
       (e.g. women's coats: DIAZ → SELENE → ...). Priority models come
       first, in order; everything else is shuffled behind them.

   [FIX 5] PER-SLOT FAMILY / COLOR FILTER OPT-OUT
       SLOT_CONFIG.familyFilter / .colorFilter. Coats default to FALSE so a
       gold DIAZ is not filtered out of an energy cart. Flip to true to
       restore v4 behaviour.

   [FIX 6] `.previewCartCollection` IS SHOWN AGAIN
       v4 called .hide() on an empty/no-rule cart and never called .show().
       Once hidden, the upsell block stayed hidden for the rest of the
       session even after the cart refilled.

   [FIX 7] ACCESSORY BACK-FILL IS SIDE-EFFECT FREE
       pickAccessories() now works from a local copy of cart accessory state,
       so parallel slot fetches cannot mutate each other's decisions.

   [FIX 8] CROSS-SLOT DEDUPE
       A product that lives in two collections can no longer be appended
       to the carousel twice.

   [FIX 9] IN-PLACE SLOT FALLBACK
       A short/empty slot is back-filled immediately at its own position,
       excluding products already appended in this build.

   [PERF]  cardMeta() results are memoized per element (was recomputed
       4-5x per card per rebuild: gender, family, color, sort).

   --------------------------------------------------------------------------
   v7 — BUSINESS RULES (these DO change what customers see)
   --------------------------------------------------------------------------
   [BR 1] COATS ARE NEVER FILTERED AWAY
       A halat must always be recommendable for a blouse+pants cart, so
       the coat slot has both hard filters off (family + colour) and uses
       colour only as a tiebreaker. Selection is:
           DIAZ (cart colour) → DIAZ (any colour)
         → SELENE (cart colour) → SELENE (any colour)
         → rest of the collection, shuffled
       Extend the order in SLOT_CONFIG.coat.priority.

   [BR 2] BONETA IS A FIRST-CLASS UPSELL SLOT
       Medical outfit flows fetch /collections/bonete-medicale directly.
       With a known outfit colour, only an EXACT canonical colour is valid:
       Blue != Royal Blue, Navy != Marine, Pink != Rose Dust, etc.
       If no exact boneta exists, or one is already in the cart, the slot is
       replaced in-place by a generic accessory.

   [BR 3] BLOUSE + PANTS COMPLETION ORDER
       The main outfit-completion flow is:
           coat → boneta → Crocs → generic accessory
       so bonete surface earlier instead of competing inside the broad
       accessories collection.

   [BR 4] GENERIC ACCESSORIES DO NOT PICK BONETE
       The `accessory` slot now prefers bottles and then other non-boneta
       accessories. This prevents duplicate bonete when boneta + accessory
       slots are fetched in parallel.

   [BR 5] FULL OUTFIT RULE
       blouse + pants + coat becomes:
           boneta → generic accessory → Crocs → matching blouse
       If a boneta is already in cart, its slot falls back to another generic
       accessory while preserving the slot position.

   --------------------------------------------------------------------------
   DETECTION MODEL — priority order for every product (unchanged intent,
   now actually enforced field-by-field):
     1. magento.* METAFIELDS (authoritative):
          emkp_pentru        → gender
          emkp_tip_produs    → type
          product_collection → family (energy/gold)
          color              → color
        - Upsell cards: stamped as data-upsell-* attributes directly in
          templates/collection.ajax-cart-upsell.liquid.
        - Cart items: fetched from /products/{handle}?view=upsell-meta
          (templates/product.upsell-meta.liquid), cached per page.
     2. product_type ("IMBRACAMINTE > FEMEI > BLUZE") from /cart.js
        or the card's data-json-product blob.
     3. tags (family only: "GOLD COLLECTION" etc.).
     4. Title keywords + whole-word brand matcher (norm ≠ norma).

   chooseRuleSet() detection conditions remain the same idea as v6; v7 changes
   the slot CONTENTS/order to promote bonete in medical outfit flows.

   DEBUG MODE:
       localStorage.setItem('upsellDebug', '1')
   then reload and open the cart. Turn off with
       localStorage.removeItem('upsellDebug')
   ========================================================================== */

(function ($) {
    'use strict';

    /* ======================================================
       TOOL POPUP STATE GUARD
       Defensive cleanup: keeps the cart-note popup and the
       .active-tool state from leaking across cart re-renders.
       Bound once per page load.
    ====================================================== */
    function bindToolPopupGuard() {
        if (window.__cartToolStateGuardBound) return;
        window.__cartToolStateGuardBound = true;

        const bind = () => {
            const sidebar = document.querySelector('#halo-cart-sidebar');
            if (!sidebar) {
                setTimeout(bind, 500);
                return;
            }

            const observer = new MutationObserver(() => {
                const activeInPopup = document.activeElement?.closest('.popup-toolDown');
                if (activeInPopup) return;

                document.querySelectorAll('.popup-toolDown.show')
                    .forEach(el => el.classList.remove('show'));
                document.querySelectorAll('.previewCart.active-tool')
                    .forEach(el => el.classList.remove('active-tool'));
            });

            observer.observe(sidebar, { childList: true, subtree: true });
        };

        bind();
    }

    /* ======================================================
       DEBUG LOGGER
    ====================================================== */
    const DEBUG = (() => {
        try { return localStorage.getItem('upsellDebug') === '1' || window.upsellDebug === true; }
        catch { return window.upsellDebug === true; }
    })();

    function dbg() {
        if (!DEBUG) return;
        // eslint-disable-next-line no-console
        console.log.apply(console, ['[upsell]'].concat([].slice.call(arguments)));
    }

    /* ======================================================
       MAIN UPSELL BUILDER
       Called every time the cart-update event fires.
    ====================================================== */
    const measuredUpsellHeights = new Map();
    const activeUpsellLayouts = new Set();
    let cartPosition = null;

    function positionCartMessage(reset = false) {
        const cart = document.querySelector('#halo-cart-sidebar .previewCart');
        if (cartPosition?.cart !== cart) {
            cartPosition?.destroy();
            cartPosition = null;
        }
        if (cartPosition) {
            if (reset) cartPosition.reset();
            return cartPosition.refresh();
        }
        const message = cart?.querySelector('.previewCartMessage');
        if (!message) return false;

        // Fill only the measured shortfall in a short cart, before notes/totals.
        const spacer = document.createElement('div');
        spacer.className = 'previewCartMessageSpacer';
        spacer.setAttribute('aria-hidden', 'true');
        const summary = cart.querySelector('.previewCartInfo');
        cart.insertBefore(spacer, cart.querySelector('.previewCartTool') || summary);
        cart.setAttribute('data-message-position', '');
        let interacted = false;
        let observer;
        const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
        const stop = () => { interacted = true; };
        events.forEach(type => cart.addEventListener(type, stop, { passive: true }));

        const position = {
            cart,
            reset() { interacted = false; },
            refresh() {
                if (!cart.isConnected) { position.destroy(); return false; }
                if (!message.getBoundingClientRect().height) {
                    spacer.style.height = '0px';
                    cart.removeAttribute('data-message-position');
                    return false;
                }
                if (interacted || !document.body.classList.contains('cart-sidebar-show') || !cart.clientHeight) return false;
                cart.setAttribute('data-message-position', '');
                const bottom = cart.getBoundingClientRect().top + cart.clientTop + cart.clientHeight;
                const currentSpace = spacer.getBoundingClientRect().height;
                const naturalBottom = message.getBoundingClientRect().bottom + cart.scrollTop - currentSpace;
                const space = Math.max(0, bottom - naturalBottom);
                if (Math.abs(space - currentSpace) > 0.5) spacer.style.height = `${space}px`;
                const delta = message.getBoundingClientRect().bottom - bottom;
                if (Math.abs(delta) > 0.5) cart.scrollTop += delta;
                return true;
            },
            destroy() {
                observer?.disconnect();
                events.forEach(type => cart.removeEventListener(type, stop));
                spacer.remove();
                cart.removeAttribute('data-message-position');
                if (cartPosition === position) cartPosition = null;
            }
        };
        cartPosition = position;
        position.refresh();
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => position.refresh());
            observer.observe(cart);
            Array.from(cart.children).forEach(child => { if (child !== spacer) observer.observe(child); });
            observer.observe(message);
        }
        return true;
    }

    function createUpsellLayout(container, grid) {
        const cart = container.closest('#halo-cart-sidebar .previewCart');
        if (!cart || !grid) return null;

        const wrapper = container.closest('.previewCartCollection');
        let observer;
        let ready = false;
        let destroyed = false;
        let userInteracted = false;
        const inputEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
        const markInteraction = () => { userInteracted = true; };
        inputEvents.forEach(type => cart.addEventListener(type, markInteraction, { passive: true }));

        const cacheKey = () => {
            const style = getComputedStyle(container);
            return [Math.round(container.getBoundingClientRect().width),
                document.documentElement.lang, style.fontFamily, style.fontSize].join('|');
        };

        const remember = height => {
            measuredUpsellHeights.set(cacheKey(), height);
            if (measuredUpsellHeights.size > 20) {
                measuredUpsellHeights.delete(measuredUpsellHeights.keys().next().value);
            }
        };

        // Anchor the content the customer is looking at, not an old scroll target.
        const captureAnchor = () => {
            const bounds = cart.getBoundingClientRect();
            const visible = element => {
                if (!element) return false;
                const rect = element.getBoundingClientRect();
                return rect.height > 0 && rect.bottom > bounds.top && rect.top < bounds.bottom;
            };
            const summary = cart.querySelector('.previewCartInfo');
            const candidates = cart.querySelectorAll('.previewCartItem, .previewCartCollection, .previewCartTool, .previewCartInfo');
            const atBottom = cart.scrollTop > 0 && cart.scrollHeight - cart.clientHeight - cart.scrollTop < 2;
            const element = (!userInteracted || atBottom) && visible(summary) ? summary : Array.from(candidates).find(visible);
            return element ? { element, top: element.getBoundingClientRect().top } : null;
        };

        const keepAnchor = (anchor, change) => {
            change();
            if (cartPosition?.cart === cart && cartPosition.refresh()) return;
            if (!anchor?.element.isConnected || !cart.isConnected ||
                !document.body.classList.contains('cart-sidebar-show')) return;
            const delta = anchor.element.getBoundingClientRect().top - anchor.top;
            if (Math.abs(delta) < 0.5) return;
            const oldBehavior = cart.style.scrollBehavior;
            cart.style.scrollBehavior = 'auto';
            cart.scrollTop += delta;
            cart.style.scrollBehavior = oldBehavior;
        };

        const updateHeight = () => {
            if (destroyed || !container.isConnected) return;
            if (container.getBoundingClientRect().width < 10) return;
            const height = Math.ceil($(grid).outerHeight(true));
            if (!height) return;
            const previous = parseFloat(container.style.getPropertyValue('--cart-upsell-height'));
            if (height !== previous) {
                keepAnchor(captureAnchor(), () => container.style.setProperty('--cart-upsell-height', `${height}px`));
            }
            remember(height);
        };

        const layout = {
            begin() {
                const height = grid.classList.contains('slick-initialized') ?
                    Math.ceil($(grid).outerHeight(true)) : measuredUpsellHeights.get(cacheKey());
                keepAnchor(captureAnchor(), () => {
                    if (height) container.style.setProperty('--cart-upsell-height', `${height}px`);
                    else container.style.removeProperty('--cart-upsell-height');
                    container.setAttribute('data-upsell-layout', 'loading');
                });
            },
            reveal() {
                if (destroyed || !container.isConnected) return;
                updateHeight();
                container.setAttribute('data-upsell-layout', 'ready');
                ready = true;
                if (typeof ResizeObserver !== 'undefined') {
                    observer = new ResizeObserver(() => {
                        if (!container.isConnected) return layout.destroy();
                        updateHeight();
                    });
                    observer.observe(grid);
                    observer.observe(container);
                }
            },
            hide() {
                keepAnchor(captureAnchor(), () => { if (wrapper) wrapper.style.display = 'none'; });
                layout.destroy();
            },
            destroy() {
                destroyed = true;
                observer?.disconnect();
                inputEvents.forEach(type => cart.removeEventListener(type, markInteraction));
                grid.removeEventListener('load', onImageLoad, true);
                window.removeEventListener('resize', onResize);
                activeUpsellLayouts.delete(layout);
            }
        };
        const onImageLoad = () => { if (ready) updateHeight(); };
        const onResize = () => { if (ready) updateHeight(); };
        grid.addEventListener('load', onImageLoad, true);
        window.addEventListener('resize', onResize);
        activeUpsellLayouts.add(layout);
        return layout;
    }

    function runUpsell(cartSnapshot) {

        activeUpsellLayouts.forEach(layout => layout.destroy());

        const containers = $('[data-ajax-cart-upsell]');

        /* ------------------------------------------------------
           [FIX 1] BUILD ID
           Bumped on every run. Any build whose id no longer
           matches window.__upsellBuildId is stale and must not
           touch the DOM.
        ------------------------------------------------------ */
        const buildId = (
            window.__upsellBuildId = (window.__upsellBuildId || 0) + 1
        );
        const isCurrentBuild = () => buildId === window.__upsellBuildId;
        if (!containers.length) return;

        dbg('=== build', buildId, 'start ===');

        /* ======================================================
           LOCALE DETECTION
        ====================================================== */

        const LOCALE = (window.Shopify?.locale || 'ro').toLowerCase();
        const LOCALE_ROOT = window.Shopify?.routes?.root || '/';

        /* ======================================================
           CONFIG — RULES (language-independent, v7 business flow)
        ====================================================== */

        const UPSELL_RULES = {

            /* =====================
               CORE MEDICAL FLOWS
            ===================== */

            top_only: [
                { type: 'pants', qty: 2 },
                { type: 'coat', qty: 1 },
                { type: 'boneta', qty: 1 }
            ],

            pants_only: [
                { type: 'top', qty: 2 },
                { type: 'coat', qty: 1 },
                { type: 'boneta', qty: 1 }
            ],

            top_and_pants: [
                { type: 'coat', qty: 2 },
                { type: 'top', qty: 1 },
                { type: 'boneta', qty: 1 },
                { type: 'shoes', qty: 1 }
            ],

            /* Full medical outfit already in cart. Complete it with
               a matching boneta first, then generic accessory + Crocs,
               and keep one matching blouse slot for cross-sell. */
            top_pants_and_coat: [
                { type: 'boneta', qty: 1 },
                { type: 'accessory', qty: 1 },
                { type: 'shoes', qty: 1 },
                { type: 'top', qty: 1 }
            ],

            top_and_coat: [
                { type: 'pants', qty: 1 },
                { type: 'boneta', qty: 1 },
                { type: 'shoes', qty: 1 },
                { type: 'accessory', qty: 1 }
            ],

            pants_and_coat: [
                { type: 'top', qty: 1 },
                { type: 'boneta', qty: 1 },
                { type: 'shoes', qty: 1 },
                { type: 'accessory', qty: 1 }
            ],

            coat_only: [
                { type: 'pants', qty: 1 },
                { type: 'jeans', qty: 1 },
                { type: 'jacket', qty: 1 },
                { type: 'shoes', qty: 1 }
            ],

            /* =====================
               LIFESTYLE-FIRST
            ===================== */

            jacket_only: [
                { type: 'pants', qty: 2 },
                { type: 'shoes', qty: 1 },
                { type: 'accessory', qty: 1 }
            ],

            jeans_only: [
                { type: 'pants', qty: 1 },
                { type: 'top', qty: 1 },
                { type: 'shoes', qty: 1 }
            ],

            /* =====================
               ACCESSORY-FIRST
            ===================== */

            accessory_only: [
                { type: 'top', qty: 1 },
                { type: 'pants', qty: 1 }
            ]
        };

        /* ======================================================
           [FIX 4] + [FIX 5]  SLOT CONFIG
           --------------------------------------------------
           Per slot type:
             familyFilter    : apply the energy/gold family filter?
             colorFilter     : apply the dominant-colour filter?
                               (a FILTER can remove candidates)
             colorPreference : rank colour-matched candidates first
                               without ever removing anyone
                               (a PREFERENCE only reorders)
             priority        : { m: [...], f: [...] } ordered model
                               names, matched whole-word against the
                               product TITLE. Models listed here are
                               served first, in this exact order.
                               Everything else goes behind them.

           Slot types not listed here keep the v4 behaviour
           (family + colour filters on, fully randomized).

           COATS: a halat must ALWAYS be recommendable, so both hard
           filters are off — a GOLD Diaz survives an ENERGY cart, and
           a navy cart can still be shown a white halat if that's all
           there is. Ordering is DIAZ → SELENE → everything else, and
           within each rank the cart's colour is preferred. So a navy
           cart gets navy DIAZ if it exists, any DIAZ if it doesn't,
           and SELENE only when no DIAZ is available at all.

           BONETA + ACCESSORIES: both are exempt from family/colour hard
           filters. Boneta exact-colour selection happens inside the dedicated
           boneta slot. Generic accessories intentionally exclude bonete and
           can therefore focus on bottles / other add-ons.
        ====================================================== */

        const SLOT_CONFIG = {
            coat: {
                familyFilter:    false,
                colorFilter:     false,
                colorPreference: true,
                priority: {
                    f: [
                        'diaz',
                        'selene'
                        // TODO: add the rest of the women's coat order here
                    ],
                    m: [
                        // TODO: men's coat priority order
                    ]
                }
            },
            boneta: {
                familyFilter:    false,
                colorFilter:     false
            },
            accessory: {
                familyFilter:    false,
                colorFilter:     false
            }
        };

        function slotConf(type) {
            return SLOT_CONFIG[type] || {};
        }

        /* ======================================================
           EXCLUDED PRODUCTS (title keywords, whole-word match)
           --------------------------------------------------
           Products whose TITLE contains one of these words are
           never shown in the upsell carousel.

           Scope per keyword:
             'all'            → excluded from every slot
             ['coat','top']   → excluded only from those slots

           NOTE: 'cloud': 'all' also empties the jacket slot in
           the coat_only rule (cloud IS the jacket line) — the
           missing spot gets back-filled with accessories. If you
           only want CLOUD out of the medical slots, scope it:
             'cloud': ['coat', 'top', 'pants']
        ====================================================== */

        const EXCLUDED_TITLE_KEYWORDS = {
            'soft':  'all',
            'cloud': 'all'
        };

        /* ======================================================
           PRODUCT FAMILIES (brand → gender)  — LAST-RESORT ONLY
           Used when neither metafields, product_type, nor tags
           resolve. 'm' = men, 'f' = women, 'u' = unisex.
           ⚠️ TODO: Verify each brand's gender. Edit as needed.
        ====================================================== */

        const PRODUCT_FAMILIES = {
            energy: {
                'rand':    'm',
                'norm':    'm',
                'ever':    'm',
                'form':    'm',
                'cos':     'm',
                'wave':    'm',
                'utility': 'm',
                'anais':   'f',
                'celeste': 'f',
                'flex':    'f',
                'frida':   'f',
                'linea':   'f',
                'lotus':   'f',
                'nevia':   'f',
                'norma':   'f',
                'rosa':    'f',
                'thalya':  'f',
                'verso':   'f',
                'soft':    'f'
            },
            gold: {
                'mira':    'f',
                'novum':   'f',
                'palazzo': 'f',
                'perla':   'f',
                'petali':  'f',
                'venus':   'f',
                'iris':    'f',
                'selene':  'f',
                'diaz':    'f',
                'oliver':  'm',
                'edric':   'm',
                'luna':    'f'
            }
        };

        /* ======================================================
           BRAND MATCHERS (precompiled)
           Whole-word, longest-first: 'norm' can NOT match inside
           "norma". Accented Latin letters count as word chars.
        ====================================================== */

        const NON_WORD = '[^a-z\\u00c0-\\u024f]';

        function wholeWordRe(word) {
            return new RegExp(
                '(^|' + NON_WORD + ')' +
                String(word).toLowerCase() +
                '($|' + NON_WORD + ')'
            );
        }

        const BRAND_MATCHERS = (() => {
            const list = [];
            for (const family in PRODUCT_FAMILIES) {
                const brands = PRODUCT_FAMILIES[family];
                for (const brand in brands) {
                    list.push({
                        brand: brand,
                        family: family,
                        gender: brands[brand],
                        re: wholeWordRe(brand)
                    });
                }
            }
            // Longest brand name first: "norma" wins over "norm"
            list.sort((a, b) => b.brand.length - a.brand.length);
            return list;
        })();

        /* ======================================================
           EXCLUSION MATCHERS (precompiled, whole-word)
        ====================================================== */

        const EXCLUDE_MATCHERS = (() => {
            const list = [];
            for (const kw in EXCLUDED_TITLE_KEYWORDS) {
                list.push({
                    kw: kw,
                    scope: EXCLUDED_TITLE_KEYWORDS[kw],
                    re: wholeWordRe(kw)
                });
            }
            return list;
        })();

        /* ======================================================
           PRIORITY MATCHER CACHE (compiled once per build)
        ====================================================== */

        const PRIORITY_RE_CACHE = new Map();

        function priorityRe(name) {
            if (!PRIORITY_RE_CACHE.has(name)) {
                PRIORITY_RE_CACHE.set(name, wholeWordRe(name));
            }
            return PRIORITY_RE_CACHE.get(name);
        }

        /**
         * A card's product TITLE only (not the description, so
         * marketing copy can't trigger a false exclusion).
         */
        function cardTitle($el) {
            const json = $el.find('[data-json-product]').data('json-product');
            let t = json?.title;
            if (!t) t = $el.find('.card-title').first().text();
            return String(t || '').toLowerCase().trim();
        }

        function productIdOf($el) {
            return $el.find('[data-json-product]').data('json-product')?.id || null;
        }

        function isExcludedFromSlot($el, slotType) {
            if (!EXCLUDE_MATCHERS.length) return false;
            const title = cardTitle($el);
            if (!title) return false;
            return EXCLUDE_MATCHERS.some(m => {
                const inScope = m.scope === 'all' ||
                    (Array.isArray(m.scope) && m.scope.includes(slotType));
                return inScope && m.re.test(title);
            });
        }

        /* ======================================================
           ACCESSORY PRIORITY (product name keywords per locale)
        ====================================================== */

        const ACCESSORY_KEYWORDS = {
            ro: ['boneta', 'sticla'],
            en: ['cap', 'bottle'],
            de: ['kappe', 'flasche']
        };

        const ACCESSORY_PRIORITY = ACCESSORY_KEYWORDS[LOCALE] || ACCESSORY_KEYWORDS['ro'];

        /* ======================================================
           MULTILINGUAL KEYWORD MAP — FALLBACK ONLY
        ====================================================== */

        const KEYWORDS = {
            top: {
                ro: ['bluza'],
                en: ['scrub top', 'top'],
                de: ['kasack', 'oberteil']
            },
            pants: {
                ro: ['pantaloni'],
                en: ['pants', 'trousers'],
                de: ['hose', 'hosen']
            },
            coat: {
                ro: ['halat'],
                en: ['lab coat', 'coat'],
                de: ['kittel', 'mantel']
            },
            jacket: {
                ro: ['jacheta', 'jacket', 'cloud'],
                en: ['jacket', 'cloud'],
                de: ['jacke', 'cloud']
            },
            jeans: {
                ro: ['jeans'],
                en: ['jeans'],
                de: ['jeans']
            },
            accessory_boneta: {
                ro: ['boneta'],
                en: ['cap', 'scrub cap'],
                de: ['kappe', 'haube']
            },
            accessory_sticla: {
                ro: ['sticla'],
                en: ['bottle', 'water bottle'],
                de: ['flasche', 'trinkflasche']
            },
            men: {
                ro: ['barbati'],
                en: ['men'],
                de: ['herren']
            }
        };

        function titleMatches(title, category) {
            const kws = KEYWORDS[category]?.[LOCALE] || KEYWORDS[category]?.['ro'] || [];
            return kws.some(kw => title.includes(kw));
        }

        /* ======================================================
           COLOR DICTIONARY (locale → canonical key)
        ====================================================== */

        /* IMPORTANT: visually related labels are still distinct SKUs/colors.
           Example: 'Alb' !== 'Alb opac', just as 'Blue' !== 'Royal Blue'. */
        const COLOR_MAP = {
            ro: {
                'alb opac':    'opaque_white',
                'alb':         'white',
                'alba':        'white',
                'albastru':    'blue',
                'amarant':     'amaranth',
                'aqua':        'aqua',
                'argintiu':    'silver',
                'bej':         'beige',
                'bloom':       'bloom',
                'blue':        'blue',
                'bordo':       'burgundy',
                'ciel':        'sky_blue',
                'grafit':      'graphite',
                'gri':         'grey',
                'jad':         'jade',
                'kiwi':        'kiwi',
                'latte':       'latte',
                'lemon':       'lemon',
                'lila':        'lilac',
                'magenta':     'magenta',
                'marine':      'marine',
                'matcha':      'matcha',
                'mocha':       'mocha',
                'navy':        'navy',
                'negru':       'black',
                'neagra':      'black',
                'olive':       'olive',
                'orchid':      'orchid',
                'plum':        'plum',
                'rose dust':   'rose_dust',
                'rosu':        'red',
                'royal blue':  'royal_blue',
                'roz':         'pink',
                'cappucino':   'cappucino',
            },
            en: {
                'opaque white':'opaque_white',
                'white opaque':'opaque_white',
                'amaranth':    'amaranth',
                'aqua':        'aqua',
                'beige':       'beige',
                'black':       'black',
                'bloom':       'bloom',
                'blue':        'blue',
                'burgundy':    'burgundy',
                'graphite':    'graphite',
                'grey':        'grey',
                'gray':        'grey',
                'jade':        'jade',
                'kiwi':        'kiwi',
                'latte':       'latte',
                'lemon':       'lemon',
                'lilac':       'lilac',
                'magenta':     'magenta',
                'marine':      'marine',
                'matcha':      'matcha',
                'mocha':       'mocha',
                'navy':        'navy',
                'olive':       'olive',
                'orchid':      'orchid',
                'pink':        'pink',
                'plum':        'plum',
                'red':         'red',
                'rose dust':   'rose_dust',
                'royal blue':  'royal_blue',
                'silver':      'silver',
                'sky blue':    'sky_blue',
                'white':       'white',
                'cappucino':   'cappucino',
            },
            de: {
                'opak weiß':   'opaque_white',
                'opak weiss':  'opaque_white',
                'amaranth':    'amaranth',
                'aqua':        'aqua',
                'beige':       'beige',
                'blau':        'blue',
                'bloom':       'bloom',
                'bordeaux':    'burgundy',
                'graphit':     'graphite',
                'grau':        'grey',
                'himmelblau':  'sky_blue',
                'jade':        'jade',
                'kiwi':        'kiwi',
                'königsblau':  'royal_blue',
                'royal blau':  'royal_blue',
                'latte':       'latte',
                'lila':        'lilac',
                'magenta':     'magenta',
                'marine':      'marine',
                'marineblau':  'navy',
                'matcha':      'matcha',
                'mokka':       'mocha',
                'oliv':        'olive',
                'orchidee':    'orchid',
                'pflaume':     'plum',
                'rosa':        'pink',
                'rose dust':   'rose_dust',
                'rot':         'red',
                'schwarz':     'black',
                'silberfarben':'silver',
                'weiß':        'white',
                'weiss':       'white',
                'zitrone':     'lemon',
                'cappucino':   'cappucino',
            }
        };

        /* ======================================================
           COLLECTION HANDLES PER LOCALE
           ⚠️  These MUST match the current Shopify collection
               URL slugs (Admin → Collections → Edit website SEO).
               If a slot logs "found: 0" in debug mode, the handle
               here is stale. Replace placeholder EN/DE handles.
        ====================================================== */

        const COLLECTION_HANDLES = {
            ro: {
                pants_m:    'pantaloni-medicali-barbati',
                pants_f:    'pantaloni-medicali-femei',
                top_m:      'bluze-medicale-barbati',
                top_f:      'bluze-medicale-femei',
                coat_m:     'halate-medicale-barbati',
                coat_f:     'halate-medicale-femei',
                boneta:     'bonete-medicale',
                jeans_m:    'jeans-barbati',
                jeans_f:    'jeans-dama',
                jacket_m:   'cloud-barbati',
                jacket_f:   'jachete-medicale-cloud-femei',
                shoes:      'incaltaminte',
                accessory:  'accesorii-medicale'
            },
            en: {
                // TODO: replace with actual Shopify EN handles
                pants_m:    'mens-scrub-pants',
                pants_f:    'womens-scrub-pants',
                top_m:      'mens-scrub-tops',
                top_f:      'womens-scrub-tops',
                coat_m:     'mens-lab-coats',
                coat_f:     'womens-lab-coats',
                boneta:     'bonete-medicale', // TODO: replace if EN uses a translated handle
                jeans_m:    'mens-jeans',
                jeans_f:    'womens-jeans',
                jacket_m:   'cloud-jackets-men',
                jacket_f:   'cloud-jackets-women',
                shoes:      'medical-shoes',
                accessory:  'medical-accessories'
            },
            de: {
                // TODO: replace with actual Shopify DE handles
                pants_m:    'arbeitshosen-herren',
                pants_f:    'arbeitshosen-damen',
                top_m:      'kasacks-herren',
                top_f:      'kasacks-damen',
                coat_m:     'kittel-mantel-herren',
                coat_f:     'kittel-mantel-damen',
                boneta:     'bonete-medicale', // TODO: replace if DE uses a translated handle
                jeans_m:    'herren-jeans',
                jeans_f:    'damen-jeans',
                jacket_m:   'cloud-jacken-herren',
                jacket_f:   'cloud-jacken-damen',
                shoes:      'schuhe',
                accessory:  'accessoires'
            }
        };

        /* ======================================================
           NORMALIZERS
        ====================================================== */

        function normGender(v) {
            const s = String(v || '').toLowerCase();
            if (!s) return null;
            if (s.includes('unisex')) return 'u';
            const isW = s.includes('femei') || s.includes('dama') ||
                        s.includes('damen') || s.includes('women');
            const isM = s.includes('barbat') || s.includes('herren') ||
                        /(^|[^a-z])men(s)?([^a-z]|$)/.test(s);
            if (isW && isM) return 'u';
            if (isM) return 'm';
            if (isW) return 'f';
            return null;
        }

        function normType(v) {
            const s = String(v || '').toLowerCase();
            if (!s) return null;
            if (s.includes('bluz') || s.includes('kasack') || s.includes('scrub top')) return 'top';
            if (s.includes('pantaloni') || s.includes('hose') || s.includes('trouser') || s.includes('scrub pant')) return 'pants';
            if (s.includes('halat') || s.includes('kittel') || s.includes('lab coat')) return 'coat';
            if (s.includes('jachet') || s.includes('jacke') || s.includes('jacket') || s.includes('cloud')) return 'jacket';
            if (s.includes('jeans') || s.includes('denim')) return 'jeans';
            if (s.includes('incaltaminte') || s.includes('saboti') || s.includes('crocs') || s.includes('schuh') || s.includes('shoe')) return 'shoes';
            if (s.includes('accesori') || s.includes('accessoir') || s.includes('accessor') || s.includes('boneta') || s.includes('sticla')) return 'accessory';
            return null;
        }

        function normFamily(v) {
            const s = String(v || '').toLowerCase();
            if (!s) return null;
            if (s.includes('energy')) return 'energy';
            if (s.includes('gold')) return 'gold';
            return null;
        }

        /**
         * Canonicalize a color value across all locale maps.
         * Exact match first, then longest-substring, then the raw
         * lowercased value (metafields aren't translated, so raw
         * values still compare consistently cart ↔ card).
         */
        function normColor(v) {
            const s = String(v || '').toLowerCase().trim();
            if (!s) return null;

            for (const loc of ['ro', 'en', 'de']) {
                if (COLOR_MAP[loc][s] !== undefined) return COLOR_MAP[loc][s];
            }
            for (const loc of ['ro', 'en', 'de']) {
                const map = COLOR_MAP[loc];
                const keys = Object.keys(map).sort((a, b) => b.length - a.length);
                for (const k of keys) {
                    if (s.includes(k)) return map[k];
                }
            }
            return s;
        }

        /** Gender + type in one go from a product_type string. */
        function parseProductType(typeStr) {
            return {
                gender: normGender(typeStr),
                type: normType(typeStr)
            };
        }

        /** Family from tags (e.g. ".../FEMEI/GOLD COLLECTION"). */
        function familyFromTags(tags) {
            if (!Array.isArray(tags)) return null;
            for (const t of tags) {
                const f = normFamily(t);
                if (f) return f;
            }
            return null;
        }

        const META_FIELDS = ['gender', 'type', 'family', 'color'];

        /**
         * [FIX 3] Merge metadata sources field-by-field, in priority
         * order. Earlier sources win per FIELD, not wholesale — the
         * behaviour the file header always claimed but never had.
         */
        function mergeMeta() {
            const out = { gender: null, type: null, family: null, color: null };
            for (let i = 0; i < arguments.length; i++) {
                const src = arguments[i];
                if (!src) continue;
                for (const k of META_FIELDS) {
                    if (!out[k] && src[k]) out[k] = src[k];
                }
            }
            return out;
        }

        function isMetaComplete(m) {
            return !!m && META_FIELDS.every(k => !!m[k]);
        }

        /**
         * Read data-upsell-* attributes (stamped directly by
         * collection.ajax-cart-upsell.liquid) from an element or any of its
         * descendants, normalized. Works for upsell cards AND cart
         * item rows. Returns null when no attribute is present.
         */
        function readMetaAttrs($el) {
            const attr = name => {
                let v = $el.attr(name);
                if (v == null || v === '') {
                    v = $el.find('[' + name + ']').first().attr(name);
                }
                return v || '';
            };

            const meta = {
                gender: normGender(attr('data-upsell-pentru')),
                type:   normType(attr('data-upsell-tip')),
                family: normFamily(attr('data-upsell-collection')),
                color:  normColor(attr('data-upsell-color'))
            };

            if (!meta.gender && !meta.type && !meta.family && !meta.color) {
                return null;
            }
            return meta;
        }

        /* ======================================================
           CART DATA (/cart.js) + METAFIELD CACHE
        ====================================================== */

        /**
         * Reuse the full cart from cart-update; fetch only when it is absent.
         * Returns Map<product_id, lineItem> (has product_type, handle).
         */
        async function fetchCartInfo() {
            try {
                const hasCartSnapshot = Array.isArray(cartSnapshot?.items) &&
                    cartSnapshot.items.every(item => item.product_id && typeof item.handle === 'string');
                const cart = hasCartSnapshot ? cartSnapshot : await fetch(`${LOCALE_ROOT}cart.js`).then(r => {
                    if (!r.ok) throw new Error('Cart request failed');
                    return r.json();
                });
                const map = new Map();
                (cart.items || []).forEach(it => map.set(it.product_id, it));
                return map;
            } catch {
                return new Map();
            }
        }

        /**
         * Cached metafield lookup via /products/{handle}?view=upsell-meta
         * (templates/product.upsell-meta.liquid). If the template is
         * missing, fallbacks kick in. Failed requests can retry next rebuild.
         */
        const META_CACHE = (window.__upsellMetaCache = window.__upsellMetaCache || new Map());

        async function getProductMeta(handle) {
            if (!handle) return null;
            const cacheKey = `${LOCALE_ROOT}products/${handle}?view=upsell-meta`;
            if (META_CACHE.has(cacheKey)) return META_CACHE.get(cacheKey);

            // Cache the in-flight request too: variants can share one product.
            const pending = fetch(cacheKey).then(r => {
                if (!r.ok) throw new Error('Product metadata request failed');
                return r.text();
            }).then(txt => {
                const json = JSON.parse(txt.trim());
                const meta = {
                    gender: normGender(json.pentru) || normGender(json.product_type),
                    type:   normType(json.tip)      || normType(json.product_type),
                    family: normFamily(json.collection) || familyFromTags(json.tags),
                    color:  normColor(json.color)
                };
                META_CACHE.set(cacheKey, meta);
                return meta;
            }).catch(() => {
                META_CACHE.delete(cacheKey);
                return null;
            });
            META_CACHE.set(cacheKey, pending);
            return pending;
        }

        /* ======================================================
           CART CONTEXT
           attrs → ?view=upsell-meta → product_type → title,
           merged field by field.
        ====================================================== */

        async function getCartContext(cartInfo) {
            const ctx = {
                hasTop: false,
                hasPants: false,
                hasCoat: false,
                hasJacket: false,
                hasJeans: false,
                hasAccessory: false,
                isMen: false,
                dominantColor: null,
                family: null,
                cartIds: new Set(),
                accessoriesInCart: new Set()
            };

            /* Pass 1 (sync): collect rows */
            const rows = [];
            $('.previewCartItem').each(function () {
                const $item = $(this);
                const title = $item
                    .find('.previewCartItem-name .text')
                    .text()
                    .toLowerCase();
                const productId = Number(
                    $item.find('[data-product]').data('product')
                );
                if (productId) ctx.cartIds.add(productId);
                rows.push({
                    title: title,
                    productId: productId,
                    info: productId ? cartInfo.get(productId) : null,
                    attrMeta: readMetaAttrs($item)
                });
            });

            /* Pass 2 (parallel): metafield lookups, cached.
               [FIX 3] We now fetch whenever the DOM attributes are
               INCOMPLETE, not just when they are entirely absent.
               Results are cached per handle for the whole page, so
               this costs at most one request per distinct product. */
            await Promise.all(rows.map(async row => {
                if (isMetaComplete(row.attrMeta)) {
                    row.viewMeta = null;
                    return;
                }
                row.viewMeta = row.info?.handle
                    ? await getProductMeta(row.info.handle)
                    : null;
            }));

            /* Pass 3 (sync): resolve + aggregate */
            rows.forEach(row => {
                const { title, info, attrMeta, viewMeta } = row;
                const fromType = parseProductType(info?.product_type);

                const fromTitle = {
                    gender: titleMatches(title, 'men') ? 'm' : null,
                    type:   typeFromTitle(title),
                    family: null,
                    color:  extractExactColor(title)
                };

                const merged = mergeMeta(attrMeta, viewMeta, fromType, fromTitle);

                const itemType   = merged.type;
                let   itemGender = merged.gender;
                const itemColor  = merged.color;
                let   itemFamily = merged.family;

                const isMedical =
                    itemType === 'top' || itemType === 'pants' || itemType === 'coat';

                /* Last-resort brand lookup: gender (medical only) + family */
                const brand = detectFamilyAndGender(title);
                if (!itemGender && isMedical && brand) itemGender = brand.gender;
                if (!itemFamily && brand) itemFamily = brand.family;

                row.resolved = {
                    type: itemType,
                    gender: itemGender,
                    family: itemFamily,
                    color: itemColor,
                    source: {
                        attrs:     !!attrMeta,
                        metaView:  !!viewMeta,
                        productType: !!(fromType.type || fromType.gender),
                        title:     !!(fromTitle.type || fromTitle.color)
                    }
                };

                /* --- Aggregate type flags --- */
                if (itemType === 'top')            ctx.hasTop = true;
                else if (itemType === 'pants')     ctx.hasPants = true;
                else if (itemType === 'coat')      ctx.hasCoat = true;
                else if (itemType === 'jacket')    ctx.hasJacket = true;
                else if (itemType === 'jeans')     ctx.hasJeans = true;
                else if (itemType === 'accessory') ctx.hasAccessory = true;

                /* --- Gender ('f' never unsets isMen; mixed carts
                       keep the men's flow, same as before) --- */
                if (itemGender === 'm') ctx.isMen = true;

                /* --- Accessory sub-type (title-based on purpose:
                       decides WHICH accessory to push next) --- */
                if (titleMatches(title, 'accessory_boneta')) {
                    ctx.hasAccessory = true;
                    ctx.accessoriesInCart.add(ACCESSORY_PRIORITY[0]);
                }
                if (titleMatches(title, 'accessory_sticla')) {
                    ctx.hasAccessory = true;
                    ctx.accessoriesInCart.add(ACCESSORY_PRIORITY[1]);
                }

                /* --- Family (first medical item that resolves) --- */
                if (isMedical && !ctx.family && itemFamily) {
                    ctx.family = itemFamily;
                }

                /* --- Dominant color (first medical/jacket/boneta) --- */
                if (
                    !ctx.dominantColor && itemColor &&
                    (isMedical || itemType === 'jacket' || titleMatches(title, 'accessory_boneta'))
                ) {
                    ctx.dominantColor = itemColor;
                }
            });

            dbg('cart items:', rows.map(r => ({
                title: r.title,
                id: r.productId,
                ...r.resolved
            })));
            dbg('cart context:', {
                hasTop: ctx.hasTop, hasPants: ctx.hasPants, hasCoat: ctx.hasCoat,
                hasJacket: ctx.hasJacket, hasJeans: ctx.hasJeans,
                hasAccessory: ctx.hasAccessory,
                isMen: ctx.isMen, family: ctx.family, color: ctx.dominantColor
            });

            return ctx;
        }

        /** FALLBACK type detection from a title (old priority order). */
        function typeFromTitle(title) {
            if (titleMatches(title, 'top'))    return 'top';
            if (titleMatches(title, 'pants'))  return 'pants';
            if (titleMatches(title, 'coat'))   return 'coat';
            if (titleMatches(title, 'jacket')) return 'jacket';
            if (titleMatches(title, 'jeans'))  return 'jeans';
            if (
                titleMatches(title, 'accessory_boneta') ||
                titleMatches(title, 'accessory_sticla')
            ) return 'accessory';
            return null;
        }

        /* ======================================================
           RULE SELECTION — conditions IDENTICAL to v4
        ====================================================== */

        function chooseRuleSet(ctx) {
            const pick = name => ({ name: name, slots: UPSELL_RULES[name] });

            /* Full outfit already in the cart → no second coat.
               Push a matching blouse + accessories instead. */
            if (ctx.hasTop && ctx.hasPants && ctx.hasCoat) {
                return pick('top_pants_and_coat');
            }
            if (ctx.hasTop && ctx.hasPants) {
                return pick('top_and_pants');
            }
            if (ctx.hasTop && ctx.hasCoat) {
                return pick('top_and_coat');
            }
            if (ctx.hasPants && ctx.hasCoat) {
                return pick('pants_and_coat');
            }

            if (ctx.hasJacket && !ctx.hasTop && !ctx.hasPants) {
                return pick('jacket_only');
            }
            if (ctx.hasJeans && !ctx.hasTop && !ctx.hasPants) {
                return pick('jeans_only');
            }

            if (ctx.hasAccessory && !ctx.hasTop && !ctx.hasPants && !ctx.hasCoat) {
                return pick('accessory_only');
            }

            if (ctx.hasTop)   return pick('top_only');
            if (ctx.hasPants) return pick('pants_only');
            if (ctx.hasCoat)  return pick('coat_only');

            return null;
        }

        /* ======================================================
           COLLECTION MAP (locale-aware)
        ====================================================== */

        function getCollectionHandle(type, isMen) {
            const handles = COLLECTION_HANDLES[LOCALE] || COLLECTION_HANDLES['ro'];

            const map = {
                pants:     isMen ? handles.pants_m    : handles.pants_f,
                top:       isMen ? handles.top_m      : handles.top_f,
                coat:      isMen ? handles.coat_m     : handles.coat_f,
                boneta:    handles.boneta || COLLECTION_HANDLES.ro.boneta,
                jeans:     isMen ? handles.jeans_m    : handles.jeans_f,
                jacket:    isMen ? handles.jacket_m   : handles.jacket_f,
                shoes:     handles.shoes,
                accessory: handles.accessory
            };

            return map[type] || null;
        }

        /* ======================================================
           UPSELL CARD METADATA (memoized per element)
           data-upsell-* attributes → json-product blob
           (product_type + tags) → brand/title fallback.
        ====================================================== */

        const CARD_META_CACHE = new WeakMap();

        function cardMeta($el) {
            const el = $el[0];
            if (el && CARD_META_CACHE.has(el)) return CARD_META_CACHE.get(el);

            const json = $el.find('[data-json-product]').data('json-product');

            const attrs = readMetaAttrs($el);

            const fromJson = json ? {
                gender: normGender(json.type),
                type:   normType(json.type),
                family: familyFromTags(json.tags),
                color:  null
            } : null;

            let meta = mergeMeta(attrs, fromJson);

            if (!meta.gender || !meta.family) {
                const b = detectFamilyAndGender(json?.title || $el.text());
                if (b) {
                    if (!meta.gender) meta.gender = b.gender;
                    if (!meta.family) meta.family = b.family;
                }
            }

            if (!meta.color) meta.color = extractExactColor($el.text());

            if (el) CARD_META_CACHE.set(el, meta);
            return meta;
        }

        /* ======================================================
           [FIX 4] DETERMINISTIC PRIORITY
           Returns the index of a card in the configured priority
           list for this slot, or MAX_SAFE_INTEGER when it is not
           a priority model.
        ====================================================== */

        function getProductPriority($el, rule, ctx) {
            const conf = slotConf(rule.type);
            const list = conf.priority?.[ctx.isMen ? 'm' : 'f'];

            if (!list || !list.length) return Number.MAX_SAFE_INTEGER;

            const title = cardTitle($el);
            if (!title) return Number.MAX_SAFE_INTEGER;

            const index = list.findIndex(name => priorityRe(name).test(title));
            return index === -1 ? Number.MAX_SAFE_INTEGER : index;
        }

        function hasPriorityList(rule, ctx) {
            const list = slotConf(rule.type).priority?.[ctx.isMen ? 'm' : 'f'];
            return !!(list && list.length);
        }

        /* ======================================================
           FETCH PRODUCTS (locale-aware URL)
        ====================================================== */

        // Share response text only within this rebuild. Product DOM and filters
        // remain separate, and the next cart update fetches fresh prices/stock.
        const collectionRequests = new Map();

        function fetchCollection(handle) {
            const url = `${LOCALE_ROOT}collections/${handle}?view=ajax-cart-upsell`;
            if (!collectionRequests.has(url)) {
                collectionRequests.set(url, fetch(url).then(async resp => {
                    if (!resp.ok) throw new Error('Upsell collection request failed');
                    return { html: await resp.text(), finalUrl: resp.url || '' };
                }));
            }
            return collectionRequests.get(url);
        }

        async function fetchProducts(rule, ctx) {
            const handle = getCollectionHandle(rule.type, ctx.isMen);
            if (!handle) return [];

            const isLifestyle = ['jeans', 'jacket', 'shoes'].includes(rule.type);
            const conf = slotConf(rule.type);
            const counts = {};

            try {
                const { html, finalUrl } = await fetchCollection(handle);

                /* GUARD: if this collection's handle was renamed,
                   Shopify's URL redirect drops the ?view= param and
                   we'd be scraping the FULL collection page — wrong
                   card layout (li.product from the main section),
                   stripped json-product, wrong everything. Skip it
                   and shout in debug so the handle gets fixed. */
                const looksLikeFullPage =
                    /<!doctype/i.test(html) || /<body[\s>]/i.test(html);
                const lostViewParam =
                    finalUrl !== '' && finalUrl.indexOf('view=ajax-cart-upsell') === -1;

                if (looksLikeFullPage || lostViewParam) {
                    dbg(`slot [${rule.type}] → /collections/${handle} returned the WRONG template — skipping`, {
                        finalUrl: finalUrl,
                        fullPage: looksLikeFullPage,
                        hint: 'handle probably renamed/redirected — update COLLECTION_HANDLES to the current slug'
                    });
                    return [];
                }

                let $products = $('<div>')
                    .html(html)
                    .find('.product');

                counts.found = $products.length;

                /* Remove products already in cart */
                $products = $products.filter(function () {
                    const id = productIdOf($(this));
                    return !ctx.cartIds.has(id);
                });
                counts.afterCartDedupe = $products.length;

                /* Excluded title keywords (SOFT, CLOUD, ...) */
                if (EXCLUDE_MATCHERS.length) {
                    $products = $products.filter(function () {
                        return !isExcludedFromSlot($(this), rule.type);
                    });
                    counts.afterExclusions = $products.length;
                }

                /* Dedicated boneta collection sanity check. Even though the
                   collection should contain only bonete, keep the slot strict. */
                if (rule.type === 'boneta') {
                    $products = $products.filter(function () {
                        const title = cardTitle($(this));
                        return titleMatches(title, 'accessory_boneta') ||
                            $(this).text().toLowerCase().includes(ACCESSORY_PRIORITY[0]);
                    });
                    counts.afterBonetaType = $products.length;
                }

                /* ------------------------------------------------------
                   [FIX 2] SLOT TYPE VALIDATION
                   The collection is trusted for MEMBERSHIP, never for
                   TYPE. A jeans product sitting inside halate-medicale-*
                   (bad automatic rule, wrong tagging, manual mistake)
                   can no longer be served as a coat.

                   Resolution order per card:
                     metadata type   → must equal the slot type
                     title detection → must equal the slot type
                     unresolved      → kept (don't empty the carousel
                                       on incomplete metadata)

                   Once every card reliably carries data-upsell-tip you
                   can tighten this to `return meta.type === rule.type;`
                ------------------------------------------------------ */
                if (['top', 'pants', 'coat', 'jeans', 'jacket'].includes(rule.type)) {
                    $products = $products.filter(function () {
                        const $p = $(this);
                        const meta = cardMeta($p);

                        if (meta.type) return meta.type === rule.type;

                        const detected = typeFromTitle(cardTitle($p));
                        if (detected) return detected === rule.type;

                        if (DEBUG) {
                            dbg(`  ⚠ [${rule.type}] unresolved type, keeping:`, cardTitle($p));
                        }
                        return true;
                    });
                    counts.afterTypeCheck = $products.length;
                }

                /* Shoes → only crocs */
                if (rule.type === 'shoes') {
                    $products = $products.filter(function () {
                        return $(this).text().toLowerCase().includes('crocs');
                    });
                    counts.afterCrocs = $products.length;
                }

                /* Gender sanity filter (medical only).
                   Unisex ('u') and unknown products are kept. */
                if (!isLifestyle && rule.type !== 'accessory') {
                    $products = $products.filter(function () {
                        const g = cardMeta($(this)).gender;
                        if (!g || g === 'u') return true;
                        return (g === 'm') === ctx.isMen;
                    });
                    counts.afterGender = $products.length;
                }

                /* Family filter (medical only, soft: keeps all when
                   nothing matches).
                   [FIX 5] SLOT_CONFIG can opt a slot out entirely —
                   coats do by default, so a GOLD DIAZ survives an
                   ENERGY cart. */
                const applyFamily =
                    ctx.family && !isLifestyle && conf.familyFilter !== false;

                if (applyFamily) {
                    const matched = $products.filter(function () {
                        return cardMeta($(this)).family === ctx.family;
                    });
                    if (matched.length) $products = matched;
                    counts.afterFamily = $products.length;
                } else if (ctx.family && !isLifestyle) {
                    counts.familyFilter = 'skipped (SLOT_CONFIG)';
                }

                /* Color filter (medical only, soft) — same opt-out */
                const applyColor =
                    ctx.dominantColor && !isLifestyle && conf.colorFilter !== false;

                if (applyColor) {
                    const matched = $products.filter(function () {
                        return cardMeta($(this)).color === ctx.dominantColor;
                    });
                    if (matched.length) $products = matched;
                    counts.afterColor = $products.length;
                } else if (ctx.dominantColor && !isLifestyle) {
                    counts.colorFilter = 'skipped (SLOT_CONFIG)';
                }

                /* ------------------------------------------------------
                   v7 BONETA SLOT
                   ------------------------------------------------------
                   - dedicated /bonete-medicale collection
                   - if a boneta is already in cart: leave slot empty so the
                     build-level missing-slot fallback replaces it with a
                     generic accessory
                   - known cart colour: exact canonical colour ONLY
                   - unknown cart colour: any boneta is acceptable
                   - no exact colour: return [] -> generic accessory back-fill
                ------------------------------------------------------ */
                if (rule.type === 'boneta') {
                    const bonetaKeyword = ACCESSORY_PRIORITY[0];

                    if (ctx.accessoriesInCart.has(bonetaKeyword)) {
                        counts.picked = 0;
                        counts.reason = 'boneta already in cart -> accessory back-fill';
                        dbg(`slot [${rule.type} x${rule.qty}] → /collections/${handle}`, counts);
                        return [];
                    }

                    let candidates = $products;

                    if (ctx.dominantColor) {
                        candidates = candidates.filter(function () {
                            return cardMeta($(this)).color === ctx.dominantColor;
                        });
                        counts.afterExactColor = candidates.length;

                        if (!candidates.length) {
                            counts.picked = 0;
                            counts.reason = `no exact boneta for ${ctx.dominantColor} -> accessory back-fill`;
                            dbg(`slot [${rule.type} x${rule.qty}] → /collections/${handle}`, counts);
                            return [];
                        }
                    }

                    const result = candidates.toArray()
                        .sort(() => 0.5 - Math.random())
                        .slice(0, rule.qty);

                    counts.picked = result.length;
                    counts.order = result.map(el => ({
                        title: cardTitle($(el)),
                        color: cardMeta($(el)).color
                    }));

                    dbg(`slot [${rule.type} x${rule.qty}] → /collections/${handle}`, counts);
                    return result;
                }

                /* Generic accessories (bonete are handled by their own slot). */
                if (rule.type === 'accessory') {
                    const picked = pickAccessories($products, rule.qty, ctx);
                    counts.picked = picked.length;
                    dbg(`slot [${rule.type} x${rule.qty}] → /collections/${handle}`, counts);
                    return picked;
                }

                /* ------------------------------------------------------
                   [FIX 4] ORDERING
                   Slots with a configured priority list are sorted
                   deterministically: priority models first in their
                   configured order, everything else shuffled behind
                   them. Slots without a list keep the v4 shuffle.
                ------------------------------------------------------ */
                const usePriority   = hasPriorityList(rule, ctx);
                const usePrefColor  = !!(conf.colorPreference && ctx.dominantColor);

                if (usePriority || usePrefColor) {
                    const decorated = $products.toArray().map(el => ({
                        el: el,
                        /* 1st key: configured model order (DIAZ, SELENE, ...) */
                        rank: usePriority
                            ? getProductPriority($(el), rule, ctx)
                            : 0,
                        /* 2nd key: cart colour match — reorders only,
                           never removes, so the slot can't come back
                           empty just because no colour matched */
                        colorRank: usePrefColor
                            ? (cardMeta($(el)).color === ctx.dominantColor ? 0 : 1)
                            : 0,
                        /* 3rd key: shuffle within a tie */
                        tie: Math.random()
                    }));

                    decorated.sort((a, b) =>
                        (a.rank - b.rank) ||
                        (a.colorRank - b.colorRank) ||
                        (a.tie - b.tie)
                    );

                    const result = decorated.slice(0, rule.qty).map(d => d.el);
                    counts.picked = result.length;
                    counts.order = decorated
                        .slice(0, rule.qty)
                        .map(d =>
                            (d.rank === Number.MAX_SAFE_INTEGER ? 'rest' : 'p' + d.rank) +
                            (usePrefColor ? (d.colorRank === 0 ? '+color' : '') : '')
                        );

                    dbg(`slot [${rule.type} x${rule.qty}] → /collections/${handle}`, counts);
                    return result;
                }

                /* Randomize */
                const shuffled = $products.toArray().sort(() => 0.5 - Math.random());
                const result = shuffled.slice(0, rule.qty);
                counts.picked = result.length;

                dbg(`slot [${rule.type} x${rule.qty}] → /collections/${handle}`, counts);
                return result;

            } catch (e) {
                dbg(`slot [${rule.type}] → /collections/${handle} FAILED`, e);
                return [];
            }
        }

        /* ======================================================
           BUILD UPSELL
        ====================================================== */

        async function buildUpsell($container) {

            const $wrapper = $container.closest('.previewCartCollection');
            const $grid = $container.find('.ajax-cart-upsell__content');
            const $loader = $container.find('.ajax-cart-upsell__loader');
            const layout = createUpsellLayout($container[0], $grid[0]);
            layout?.begin();

            if ($grid.hasClass('slick-initialized')) {
                $grid.slick('unslick');
            }

            $grid.empty().css('visibility', 'hidden');
            $container.attr('aria-busy', 'true');
            $loader.removeAttr('hidden').addClass('is-visible').show();
            $wrapper.show();

            const finishLoading = () => {
                $loader.removeClass('is-visible').hide();
                $container.removeAttr('aria-busy');
            };

            cartPosition?.refresh();

            /* --- await #1 --- */
            const cartInfo = await cartInfoPromise;
            if (!isCurrentBuild()) {
                dbg('discarding stale build', buildId, '(after cart.js)');
                return;
            }

            /* --- await #2 --- */
            const ctx = await cartContextPromise;
            if (!isCurrentBuild()) {
                dbg('discarding stale build', buildId, '(after cart context)');
                return;
            }

            const chosen = chooseRuleSet(ctx);
            dbg('chosen rule:', chosen ? chosen.name : null);

            if (!chosen) {
                finishLoading();
                return layout ? layout.hide() : $wrapper.hide();
            }

            const rules = chosen.slots;

            // Defensive cleanup of stale tool-popup state from previous renders
            document.querySelectorAll('.popup-toolDown.show')
                .forEach(el => el.classList.remove('show'));
            document.querySelectorAll('.previewCart.active-tool')
                .forEach(el => el.classList.remove('active-tool'));

            /* Parallel fetch */
            const fetchJobs = rules.map(rule =>
                fetchProducts(rule, ctx).then(items => {
                    // Start a needed backfill while the slower slots finish.
                    // Selection still happens in the original slot order below.
                    if (isCurrentBuild() && items.length < rule.qty) {
                        const fallbackHandle = getCollectionHandle('accessory', ctx.isMen);
                        if (fallbackHandle) fetchCollection(fallbackHandle).catch(() => {});
                    }
                    return { rule, items };
                })
            );

            /* --- await #3 --- */
            const results = await Promise.all(fetchJobs);
            if (!isCurrentBuild()) {
                dbg('discarding stale build', buildId, '(after slot fetches)');
                return;
            }

            /* [FIX 8] cross-slot dedupe by product id */
            const seenIds = new Set();

            const appendItems = items => {
                let appended = 0;
                items.forEach(el => {
                    const id = productIdOf($(el));
                    if (id) {
                        if (seenIds.has(id)) return;
                        seenIds.add(id);
                    }
                    $grid.append(el);
                    appended++;
                });
                return appended;
            };

            /* Append in business-rule order. If a slot is short (most
               importantly a boneta slot that intentionally returned []),
               back-fill THAT SLOT immediately with generic accessories.
               This preserves carousel order and prevents duplicate bottles
               by excluding everything already appended in this build. */
            for (const { rule, items } of results) {
                const appended = appendItems(items);
                const missing = Math.max(0, rule.qty - appended);

                if (!missing) continue;

                dbg(`slot [${rule.type}] missing:`, missing, '→ accessory fallback in-place');

                const backfillCtx = {
                    ...ctx,
                    cartIds: new Set([...ctx.cartIds, ...seenIds])
                };

                const extra = await fetchProducts(
                    { type: 'accessory', qty: missing },
                    backfillCtx
                );

                if (!isCurrentBuild()) {
                    dbg('discarding stale build', buildId, '(during in-place accessory fallback)');
                    return;
                }

                appendItems(extra);
            }

            dbg('final grid items:', $grid.children().length);

            if (!$grid.children().length) {
                finishLoading();
                return layout ? layout.hide() : $wrapper.hide();
            }

            /* [FIX 6] the wrapper may have been hidden by an earlier
               empty-cart render — v4 never showed it again. */
            $wrapper.show();

            /* Slick init */
            waitForLayout($grid, () => {

                /* The layout wait is async too — a newer build may
                   have started (and already unslicked/emptied the
                   grid) while we were waiting. */
                if (!isCurrentBuild()) {
                    dbg('discarding stale build', buildId, '(before slick init)');
                    return;
                }

                if ($grid.hasClass('slick-initialized')) {
                    $grid.slick('unslick');
                }

                $grid.slick({
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    infinite: false,
                    arrows: true,
                    dots: false,
                    nextArrow: window.arrows?.icon_next,
                    prevArrow: window.arrows?.icon_prev,
                    rtl: window.rtl_slick
                });

                requestAnimationFrame(() => {
                    if (!isCurrentBuild()) return;

                    $grid.slick('setPosition');
                    layout?.reveal();
                    finishLoading();
                    $grid.css('visibility', 'visible');
                });
            });
        }

        /* ======================================================
           INIT — reuse event data or fetch cart once; share context across
           containers and rebuild every container on every update.
        ====================================================== */

        const cartInfoPromise = fetchCartInfo();
        const cartContextPromise = cartInfoPromise.then(getCartContext);

        containers.each(function () {
            buildUpsell($(this));
        });

        /* ======================================================
           HELPERS
        ====================================================== */

        /**
         * LAST-RESORT: detect family + gender from a title.
         * Whole-word, longest-brand-first ("norma" ≠ "norm").
         */
        function detectFamilyAndGender(text) {
            text = String(text || '').toLowerCase();
            for (const m of BRAND_MATCHERS) {
                if (m.re.test(text)) {
                    return { family: m.family, gender: m.gender };
                }
            }
            return null;
        }

        /**
         * Pick GENERIC accessories only. Bonete are intentionally excluded
         * because v7 gives them a dedicated `boneta` slot/collection.
         * Bottles are preferred; remaining non-boneta accessories are used
         * as randomized fallback candidates.
         */
        function pickAccessories($products, qty, ctx) {
            const out = [];
            const outIds = new Set();

            /* Work on a local copy. Never mutate cart context from a slot fetch;
               several slots are fetched in parallel. */
            const used = new Set(ctx.accessoriesInCart);

            const bonetaKeyword = ACCESSORY_PRIORITY[0];
            const sticlaKeyword = ACCESSORY_PRIORITY[1];

            const textOf = el => $(el).text().toLowerCase();
            const isBoneta = el => {
                const text = textOf(el);
                return text.includes(bonetaKeyword) ||
                    ['boneta', 'scrub cap', ' cap ', 'kappe', 'haube']
                        .some(k => (` ${text} `).includes(k));
            };
            const isSticla = el => textOf(el).includes(sticlaKeyword);

            const take = el => {
                if (!el) return false;

                const id = productIdOf($(el));
                if (id) {
                    if (outIds.has(id)) return false;
                    outIds.add(id);
                }

                out.push(el);
                return true;
            };

            /* ==========================================================
               1. GENERIC ACCESSORY PRIORITY: STICLA

               Bonete are deliberately NOT considered here anymore.
               The dedicated `boneta` slot owns boneta recommendations.
            ========================================================== */
            if (out.length < qty && !used.has(sticlaKeyword)) {
                const bottles = $products.filter(function () {
                    return isSticla(this) && !isBoneta(this);
                });

                if (bottles.length) {
                    const bottleArray = bottles.toArray()
                        .sort(() => 0.5 - Math.random());
                    const chosenBottle = bottleArray[0];

                    if (take(chosenBottle)) {
                        used.add(sticlaKeyword);
                        dbg('accessory: bottle selected', cardTitle($(chosenBottle)));
                    }
                }
            }

            /* ==========================================================
               2. FALLBACK: OTHER NON-BONETA ACCESSORIES

               Never re-introduce a boneta here. This keeps the dedicated
               boneta slot deterministic and prevents duplicate bonete when
               boneta + accessory slots are fetched in parallel.
            ========================================================== */
            if (out.length < qty) {
                let rest = $products.toArray().filter(el => {
                    const id = productIdOf($(el));

                    if (id && outIds.has(id)) return false;
                    if (isBoneta(el)) return false;

                    return true;
                });

                /* Randomize generic accessories behind the bottle priority. */
                rest = rest.sort(() => 0.5 - Math.random());

                for (const el of rest) {
                    if (out.length >= qty) break;
                    take(el);
                }
            }

            return out.slice(0, qty);
        }

        /**
         * Extract color from free text → canonical color key.
         * "negru" (RO), "black" (EN), "schwarz" (DE) all → "black".
         */
        function extractExactColor(text) {
            text = String(text || '').toLowerCase();

            const localeColors = COLOR_MAP[LOCALE] || COLOR_MAP['ro'];

            // Longest key first so "royal blue" wins over "blue"
            const sortedKeys = Object.keys(localeColors)
                .sort((a, b) => b.length - a.length);

            for (const colorName of sortedKeys) {
                if (text.includes(colorName)) {
                    return localeColors[colorName];
                }
            }

            return null;
        }

        function waitForLayout($el, cb) {
            let tries = 0;

            const check = () => {
                const w = $el.outerWidth();
                if (w > 10 || tries > 10) {
                    cb();
                } else {
                    tries++;
                    requestAnimationFrame(check);
                }
            };

            check();
        }

    }

    /* ======================================================
       BOOTSTRAP
       Listen for the cart-update event dispatched by
       theme.js's halo.updateSidebarCart() complete callback.

       [FIX 1] Bursts of cart-update events are coalesced into a
       single rebuild. Any run that still overlaps is neutralised
       by the build-id guard inside runUpsell().
    ====================================================== */

    const REBUILD_DEBOUNCE_MS = 50;
    let rebuildTimer = null;

    function scheduleUpsell(event) {
        positionCartMessage();
        clearTimeout(rebuildTimer);
        const cartSnapshot = event?.detail;
        rebuildTimer = setTimeout(() => runUpsell(cartSnapshot), REBUILD_DEBOUNCE_MS);
    }

    $(document).ready(function () {
        bindToolPopupGuard();

        document.addEventListener('cart-update', scheduleUpsell);
        let wasOpen = document.body.classList.contains('cart-sidebar-show');
        new MutationObserver(() => {
            const isOpen = document.body.classList.contains('cart-sidebar-show');
            if (isOpen && !wasOpen) positionCartMessage(true);
            wasOpen = isOpen;
        }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

        // Edge case: sidebar somehow already open at page load
        if (document.body.classList.contains('cart-sidebar-show')) {
            scheduleUpsell();
        }
    });

})(jQuery);
